import React, { useEffect, useState } from "react";
import { Box, Typography, LinearProgress, CircularProgress, Grid, useTheme, Alert } from "@mui/material";
import { alpha } from "@mui/material/styles";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import BuildIcon from "@mui/icons-material/Build";
import API_BASE from "../config";
import { monoFontSx } from "../ThemeContext";

function MaintenanceRecommendations({ user, selectedCar }) {
  const theme = useTheme();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedCar && user) {
      fetchRecommendations();
    }
  }, [selectedCar, user]);

  const fetchRecommendations = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${API_BASE}/recommendations?vehicleId=${selectedCar.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to fetch recommendations: ${response.status} ${text}`);
      }

      const data = await response.json();
      setRecommendations(data.recommendations || []);
    } catch (err) {
      console.error("Error fetching recommendations:", err);
      setError(err.message);
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  };

  // Status color is a light "wash" background rather than a saturated card
  // fill -- lets normal text.primary/text.secondary stay readable everywhere
  // instead of needing a white-on-color branch per status.
  const statusPaletteKey = (status) => {
    switch (status) {
      case "overdue":
        return "error";
      case "upcoming":
        return "warning";
      case "good":
        return "success";
      default:
        return "info";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "overdue":
        return <WarningIcon fontSize="small" sx={{ color: theme.palette.error.main }} />;
      case "upcoming":
        return <InfoIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />;
      case "good":
        return <CheckCircleIcon fontSize="small" sx={{ color: theme.palette.success.main }} />;
      default:
        return <BuildIcon fontSize="small" sx={{ color: theme.palette.info.main }} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "overdue":
        return "Overdue";
      case "upcoming":
        return "Due Soon";
      case "good":
        return "Good";
      default:
        return "No History";
    }
  };

  const getProgressColor = (status) => {
    switch (status) {
      case "overdue":
        return "error";
      case "upcoming":
        return "warning";
      case "good":
        return "success";
      default:
        return "info";
    }
  };

  const sortedRecommendations = [...recommendations].sort((a, b) => {
    const statusOrder = { overdue: 0, upcoming: 1, good: 2, unknown: 3 };
    return statusOrder[a.status] - statusOrder[b.status];
  });

  const visibleRecommendations = sortedRecommendations.filter((rec) => rec.status !== "unknown");
  const noHistoryCount = recommendations.filter((rec) => rec.status === "unknown").length;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const overdueCount = recommendations.filter((r) => r.status === "overdue").length;
  const upcomingCount = recommendations.filter((r) => r.status === "upcoming").length;

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <BuildIcon sx={{ fontSize: 24, color: "primary.main" }} />
        <Typography variant="h6" fontWeight={800}>
          Maintenance Forecast
        </Typography>
      </Box>

      {/* Alert Section */}
      {overdueCount > 0 && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} icon={<WarningIcon />}>
          <Typography fontWeight="bold">
            {overdueCount} service{overdueCount !== 1 ? "s" : ""} overdue! Complete them soon.
          </Typography>
        </Alert>
      )}

      {upcomingCount > 0 && overdueCount === 0 && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }} icon={<InfoIcon />}>
          <Typography fontWeight="bold">
            {upcomingCount} service{upcomingCount !== 1 ? "s" : ""} due soon. Plan ahead!
          </Typography>
        </Alert>
      )}

      {error ? (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography fontWeight="bold">Unable to load maintenance forecast.</Typography>
          <Typography sx={{ mt: 1, fontSize: "0.95rem" }}>{error}</Typography>
        </Alert>
      ) : visibleRecommendations.length === 0 && noHistoryCount > 0 ? (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography fontWeight="bold">
            {noHistoryCount} service type{noHistoryCount !== 1 ? "s" : ""} have no history yet.
          </Typography>
          <Typography sx={{ mt: 1, fontSize: "0.95rem" }}>
            Add maintenance records to generate a smarter forecast.
          </Typography>
        </Alert>
      ) : overdueCount === 0 && upcomingCount === 0 ? (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} icon={<CheckCircleIcon />}>
          <Typography fontWeight="bold">All services are in good condition for now!</Typography>
        </Alert>
      ) : null}

      {noHistoryCount > 0 && visibleRecommendations.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {noHistoryCount} other service type{noHistoryCount !== 1 ? "s" : ""} currently have no history.
          </Typography>
        </Box>
      )}

      <Grid container spacing={1.5}>
        {visibleRecommendations.map((rec, idx) => {
          const key = statusPaletteKey(rec.status);
          const icon = getStatusIcon(rec.status);
          const label = getStatusLabel(rec.status);
          const progressColor = getProgressColor(rec.status);

          return (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Box
                sx={{
                  height: "100%",
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: (t) => alpha(t.palette[key].main, t.palette.mode === "dark" ? 0.10 : 0.07),
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                  {icon}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      color: `${key}.main`,
                      textTransform: "uppercase",
                      fontSize: "0.72rem",
                      letterSpacing: 0.5,
                    }}
                  >
                    {label}
                  </Typography>
                </Box>

                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                  {rec.service_type}
                </Typography>

                {rec.last_service_mileage !== null ? (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5, ...monoFontSx }}>
                      Last service: {rec.last_service_mileage.toLocaleString()} mi
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", ...monoFontSx }}>
                      Every {rec.interval.toLocaleString()} mi
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
                    No service history yet
                  </Typography>
                )}

                {rec.last_service_mileage !== null && (
                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                      <Typography variant="caption" fontWeight="bold" sx={{ color: "text.secondary" }}>
                        Progress to next service
                      </Typography>
                      <Typography variant="caption" fontWeight="bold" sx={{ color: "text.primary", ...monoFontSx }}>
                        {Math.abs(rec.miles_remaining).toLocaleString()} mi
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.max(0, ((rec.interval - rec.miles_remaining) / rec.interval) * 100))}
                      color={progressColor}
                      sx={{
                        height: 6,
                        borderRadius: 4,
                        backgroundColor: (t) => alpha(t.palette.text.primary, 0.08),
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default MaintenanceRecommendations;
